import { RuleConfigSubtaskTypeModel } from "../vetting/vetting.models";
import { BaseResponse, BaseUnitOfWorkChangeRequest, PropertyMetaModel, SelectedItemsRequest } from "../basics/basics.models";
import { BaseValidationResponse } from "../validations/validations.models";
import { QueryFilter } from "../queries/queries.models";
import { RecordTypeEnum } from "../rightstore/rightstore.models";

export interface RuleCollectionModel {
    ruleCollectionId:number;
    name:string;
    isActive:boolean;
    businessUnitId:number;
    displayName:string;
    notes: string;
    icon:string;
    vettingTypeId:number;
    ruleCollectionInputVM?:string;
    ruleCollectionUITemplateId: number;
    ruleCollectionUIInputTemplateId?: number;
    ruleCollectionUIListTemplateId?: number;
    ruleCollectionRuleCollections:RuleCollectionRuleCollectionModel[];
    ruleCollectionRuleConfigs:RuleCollectionRuleConfigModel[];
    businessUnitRuleCollections:BusinessUnitRuleCollectionModel[];
    windwardClientId: string;
    windwardClientSecret: string;
    ruleCollectionPrompt: string;
}

export interface GetBuRuleCollectionModel
{
    ruleCollectionId: number;
    name: string;
    displayName: string;
    icon: string;
    vettingTypeId: number;
    ruleCollectionInputVM: string;
    businessUnitName?: string;
}

export interface RuleCollectionClientModel
{
    name: string;
    displayName: string;
    notes: string;
    ruleCollectionId: number;
    vettingTypeId: number;
    businessUnitName?: string;
}

export interface BusinessUnitRuleCollectionModel {
    businessUnitRuleCollectionId?:number;
    ruleCollectionId?:number;
    businessUnitId:number;
    isInternal:boolean;
    ruleCollectionPrompt:string;
}

export interface RuleCollectionRuleCollectionModel {
    ruleCollectionRuleCollectionId:number;
    ruleCollectionId?:number;
    childRuleCollectionId:number;
}

export interface RuleCollectionRuleConfigModel {
    ruleCollectionRuleConfigId:number;
    ruleCollectionId?:number;
    ruleConfigId:number;
    isCritical:boolean;
}

export interface RuleConfigModel {
    failedVettingComment: string;
    failedRuleActionId: number;
    failedRuleSuggestionId: number;
    alwaysRequireRightShipReview: boolean;
    onlyRequireRightShipReviewWhenFailNa: boolean;
    neverRequireRightShipReview: boolean;
    isNa: boolean;
    applyAlways: boolean;
    shouldExecutePrefetch: boolean;
    ruleCode: string;
    shouldExecutePrefetchBeforeWhen: boolean;
    sequenceNumber: number;
    inverseOutcome: boolean;
    isActive: boolean;
    ruleConfigId?: number;
    name: string;
    description?: string;
    ruleCategoryId?: number;
    ruleSeverityId?: number;
    passedRuleSuggestionId?: number;
    passedRuleActionId?: number;
    ruleConfigSubtaskTypes?: RuleConfigSubtaskTypeModel[];
    ruleDisplayConfigs: RuleDisplayConfigModel[];
    rulePromptConfigs: RulePromptConfigModel[];
    ruleActivityId?: number;
    vettingAction?: string;
    passedVettingComment?: string;
    whenClauses?: string
    ifClauses?: string
    whenClausesSql?: string
    ifClausesSql?: string
    passedMeta?: string
    failedMeta?: string
    isVisibleToRsVetSupersOnly: boolean;
    isInfoOnlyRule: boolean;
    infoVettingComment?: string;
    prefetchRuleActionId: number;
    prefetchMeta: string;
    prefetchTimeToWaitMinutes: number;
    ruleConfigRiskCategories: RuleConfigRiskCategoryModel[];
    prefetchLastUpdateLimitDays: number;
    prefetchUpdateTriggerDays: number;
    screeningOverviewDisplayVisibilityId: number;
    screeningCriteriaDisplayVisibilityId: number;
    vettingOverviewDisplayVisibilityId: number;
    vettingCriteriaDisplayVisibilityId: number;
    coerceFinalOutcome?: boolean;
}

export interface RuleConfigRiskCategoryModel {
    ruleConfigRiskCategoryId?: number;
    id?: number;
    riskCategoryId: number;
    ruleConfigId?: number;
    sequenceNumber?: number;
    riskLevelId?: number;
    selected?: boolean;
    name?: string;
}

export enum RiskCategoryLevelEnum {
    BusinessUnit = 1,
    Customer = 2
}

export enum RuleDisplayConfigVisibilitiesEnum {
    None = 1,
    AllOutcomes = 2,
    OnlyFailed = 3
}

export interface RuleDisplayConfigModel {
    ruleDisplayConfigId: number;
    ruleConfigId: number;
    displayClauses: string;
    displayClausesSql: string;
    displayHtml: string;
    entityUri: string;
    scopeProperty: string;
    isFreeText: boolean;
    title: string;

    //Used only for UI bindings
    displayQueryFilters: QueryFilter[];
    variables: PropertyMetaModel[];
}

export interface RulePromptConfigModel {
    rulePromptConfigId: number;
    ruleConfigId: number;
    prompt: string;
    title: string;
    vetTaskOutcomeId: number;
    scopeProperty: string;
    entityUri: string;
    clauses: string;
    sqlParameters: string;
    clausesSql: string;
    result?: string;
    fullPrompt?: string;
    resultParameters?: string[];
    parameters?: { [index:string]:any};

    //Used only for UI bindings
    queryFilters: QueryFilter[];
}

export interface RuleDisplayResultSection {
    title: string;
    items: RuleDisplayResultItem[];
}

export interface RuleDisplayResultItem {
    text: string;
    uri: string;
}

export enum AggregateOperator {
    Count = 1,
    Sum = 2,
    Min = 3,
    Max = 4,
    Avg = 5,
    First = 6,
    Last = 7,
    PartitionedCount = 8,
    FirstBy = 9,
    LastBy = 10
}

export interface BusinessUnitRuleConfigModel {
    ruleConfigId: number;
    businessUnitId: number;
    isVettingRule: boolean;
    isCriticalRule: boolean;
    isAutoScreeningRule: boolean;
    vettingActionOverride: string;
}

export interface ReorderBusinessUnitRuleConfigRequest {
    ruleCollectionRuleConfigId: number;
    businessUnitId: number;
    ruleCollectionId: number;
    sequenceNumber: number;
}

export interface AssignRuleCollectionsRequest extends SelectedItemsRequest {
    assignedBusinessUnitIds: number[];
    assignedBus: BusinessUnitRuleCollectionModel[];
}

export interface SaveRuleConfigResponse extends BaseValidationResponse { }

export interface SaveRuleCollectionResponse extends BaseValidationResponse { }

export interface SaveRuleCollectionAiPrompt extends BaseValidationResponse { }

export interface BaseRuleRiskSettingModel {
    riskCategoryName: string;
    riskCategoryId: number;
    lowScore: number;
    mediumScore: number;
    highScore: number;
    ongoingMonitoringDays: number;
}


export interface AssignBuRuleCollectionsRequest
{
    businessUnitId: number;
    assignedRuleCollectionIds?: number[];
    unassignedRuleCollectionIds?: number[];
    isInternal?: boolean;
    ruleCollectionPrompt?: string;
}

export interface AssignRuleCollectionsToRuleRequest
{
    ruleId: number;
    assignedRuleCollections?: { ruleCollectionId: number, isCritical?: boolean}[];
    unassignedRuleCollectionIds?: number[];
}

export interface RuleCollectionRightStoreSetupModel
{
    ruleCollectionRightStoreSetupId?: number;
    ruleCollectionId: number;
    description: string;
    tags: RuleCollectionRightStoreSetupTagModel[];
    rightStoreProductCode: string;
    rightStoreProductUrl: string;
    imageUrl: string;
    isActive: boolean;
    isInternal: boolean;
    recordType: RecordTypeEnum;
}

export interface RuleCollectionRightStoreSetupTagModel
{
    ruleCollectionRightStoreSetupTagId?: number;
    ruleCollectionRightStoreSetupId?: number;
    rightStoreSetupTagId?: number;
}

export interface SaveRuleCollectionRightStoreSetupRequest extends BaseUnitOfWorkChangeRequest 
{
}

export interface SaveRuleCollectionRightStoreSetupResponse extends BaseResponse 
{
    rightStoreSetup: RuleCollectionRightStoreSetupModel;
}

export interface GetRuleCollectionRightStoreSetupResponse
{
    rightStoreSetup: RuleCollectionRightStoreSetupModel;
}

export interface GetRuleCollectionRightStoreSetupsResponse
{
    rightStoreSetups: RuleCollectionRightStoreSetupClientModel[];
}

export interface RuleCollectionRightStoreSetupClientModel
{
    ruleCollectionRightStoreSetupId?: number;
    ruleCollectionId: number;
    ruleCollectionName: string;
    description: string;
    tags: string[];
    rightStoreProductUrl: string;
    rightStoreProductCode?: string;
    imageUrl: string;
    isComingSoon?: boolean;
    isInternal: boolean;
    ruleCollectionPrompt: string;
}

export interface RuleCollectionAiPromptRequest {
    businessUnitId?: number;
    ruleCollectionId: number;
    ruleCollectionPrompt: string;
}

export interface RuleCollectionAiPromptResponse {
    businessUnitId?: number;
    ruleCollectionId: number;
    ruleCollectionPrompt: string;
}